import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useCallback, useEffect, useLayoutEffect, useMemo, useRef, } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Button, Chip, Stack, Typography, } from '@mui/material';
import { Check as CheckIcon, ChevronLeft as BackIcon, ChevronRight as NextIcon, } from '@mui/icons-material';
import { DEFAULT_WIZARD_STEPS, imageAssetKeyFromConfig, isShoeSizeStep, } from './wizardData';
import { getFormattedTextHtml, getFormattedTextPlain, sanitizeFormattedTextHtml, } from './formattedText';
import { useConversationStore } from '../../ConversationStoreProvider';
const defaultSteps = DEFAULT_WIZARD_STEPS;
/**
 * Step content only: scrolls inside the flex middle; not the step counter or Back/Next row.
 * Root card uses maxHeight: 100% so the composer flex column caps height; messages keep leftover space.
 */
const wizardStepBodyScrollSx = {
    flex: 1,
    minHeight: 0,
    alignSelf: 'stretch',
    overflowX: 'hidden',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    touchAction: 'pan-y',
    WebkitOverflowScrolling: 'touch',
    pr: 0.5,
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(0,0,0,0.22) rgba(0,0,0,0.06)',
    '&:hover': {
        scrollbarColor: 'rgba(0,0,0,0.35) rgba(0,0,0,0.08)',
    },
    '&::-webkit-scrollbar': {
        width: 8,
    },
    '&::-webkit-scrollbar-track': {
        background: 'rgba(0,0,0,0.06)',
        borderRadius: 4,
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0,0,0,0.28)',
        borderRadius: 4,
    },
    '&:hover::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0,0,0,0.45)',
    },
};
/**
 * Which image to show beside the question: question-level by default; when a selected
 * option has `imageUrl` or `imageAssetKey`, that wins (first matching option for multiple choice).
 */
function resolveQuestionDisplayImage(q, value) {
    const qAsset = imageAssetKeyFromConfig(q);
    const qUrl = q.imageUrl?.trim();
    if (q.selectType !== 'multiple') {
        const val = typeof value === 'string' ? value : '';
        if (val) {
            const opt = q.options.find((o) => o.value === val);
            if (opt?.imageUrl?.trim())
                return { directUrl: opt.imageUrl.trim() };
            const optKey = imageAssetKeyFromConfig(opt);
            if (optKey)
                return { assetKey: optKey };
        }
        if (qUrl)
            return { directUrl: qUrl };
        if (qAsset)
            return { assetKey: qAsset };
        return {};
    }
    const selected = Array.isArray(value) ? value : [];
    for (const opt of q.options) {
        const optKey = imageAssetKeyFromConfig(opt);
        if (!optKey && !opt.imageUrl?.trim())
            continue;
        if (selected.includes(opt.value)) {
            if (opt.imageUrl?.trim())
                return { directUrl: opt.imageUrl.trim() };
            if (optKey)
                return { assetKey: optKey };
        }
    }
    if (qUrl)
        return { directUrl: qUrl };
    if (qAsset)
        return { assetKey: qAsset };
    return {};
}
/** Image for one option, falling back to the question-level default image. */
function resolveOptionOrQuestionImage(q, opt) {
    if (opt) {
        if (opt.imageUrl?.trim())
            return { directUrl: opt.imageUrl.trim() };
        const k = imageAssetKeyFromConfig(opt);
        if (k)
            return { assetKey: k };
    }
    const qUrl = q.imageUrl?.trim();
    if (qUrl)
        return { directUrl: qUrl };
    const qAsset = imageAssetKeyFromConfig(q);
    if (qAsset)
        return { assetKey: qAsset };
    return {};
}
/** Image for a shoe-step option chip, falling back to step-level image. */
function resolveShoeOptionOrStepImage(step, opt) {
    if (opt) {
        if (opt.imageUrl?.trim())
            return { directUrl: opt.imageUrl.trim() };
        const k = imageAssetKeyFromConfig(opt);
        if (k)
            return { assetKey: k };
    }
    const u = step.imageUrl?.trim();
    if (u)
        return { directUrl: u };
    const k = imageAssetKeyFromConfig(step);
    if (k)
        return { assetKey: k };
    return {};
}
/** Same visual image source (URL or asset key) — avoids hover flicker when preview matches selection/default. */
function imageKeysEqual(a, b) {
    return (a.directUrl ?? '').trim() === (b.directUrl ?? '').trim()
        && (a.assetKey ?? '').trim() === (b.assetKey ?? '').trim();
}
function optionHasConfiguredImage(opt) {
    if (!opt)
        return false;
    if (opt.imageUrl?.trim())
        return true;
    return Boolean(imageAssetKeyFromConfig(opt));
}
/** True if this question step or any of its options may show an image. */
function questionStepHasAnyConfiguredImage(q) {
    if (q.imageUrl?.trim())
        return true;
    if (imageAssetKeyFromConfig(q))
        return true;
    return q.options.some(optionHasConfiguredImage);
}
/** True if the shoe step or any gender/width option may show an image. */
function shoeStepHasAnyConfiguredImage(s) {
    if (s.imageUrl?.trim())
        return true;
    if (imageAssetKeyFromConfig(s))
        return true;
    if (s.genderOptions.some(optionHasConfiguredImage))
        return true;
    const widthRows = Object.values(s.widthOptions);
    for (let i = 0; i < widthRows.length; i += 1) {
        if (widthRows[i].some(optionHasConfiguredImage))
            return true;
    }
    return false;
}
/** If any step defines images, reserve this pane width/height on every step to limit layout shift. */
function wizardHasAnyConfiguredImageMedia(steps) {
    return steps.some((st) => (isShoeSizeStep(st)
        ? shoeStepHasAnyConfiguredImage(st)
        : questionStepHasAnyConfiguredImage(st)));
}
/** Wraps the step image: width-capped, max 400px tall — no fill/border (avoid letterboxing a fixed box). */
const wizardImagePaneLayoutSx = {
    flex: { xs: '1 1 auto', md: '0 0 auto' },
    width: { xs: '100%', md: 'min(40vw, 360px)' },
    maxWidth: { xs: '100%', md: 'min(40vw, 360px)' },
    flexShrink: 0,
    alignSelf: { xs: 'stretch', md: 'flex-start' },
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: 'transparent',
    border: 'none',
    outline: 'none',
    boxShadow: 'none',
    overflow: 'visible',
};
/** Reserve slot while asset URL loads — same width as loaded image, fixed height to limit layout shift. */
const wizardImagePaneInvisibleReserveSx = {
    ...wizardImagePaneLayoutSx,
    height: 200,
    visibility: 'hidden',
    pointerEvents: 'none',
};
function selectedOptionDescription(q, value) {
    const isMulti = q.selectType === 'multiple';
    if (!isMulti) {
        const val = typeof value === 'string' ? value : '';
        if (!val)
            return '';
        return q.options.find((o) => o.value === val)?.description?.trim() ?? '';
    }
    const selected = Array.isArray(value) ? value : [];
    for (const v of selected) {
        const d = q.options.find((o) => o.value === v)?.description?.trim();
        if (d)
            return d;
    }
    return '';
}
/**
 * Renders step image: uses `directUrl` when provided (e.g. converse-app SSR enrichment), else
 * resolves `assetKey` via the conversation store (portal embed with PortalStore.getOrLoad).
 */
const WizardStepImage = observer(({ assetKey, directUrl }) => {
    const store = useConversationStore();
    const trimmed = assetKey?.trim() ?? '';
    const resolvedDirect = directUrl?.trim();
    const asset = trimmed && !resolvedDirect
        ? store.getOrLoad?.('asset', trimmed)
        : undefined;
    // Read MST observables during render so this observer re-renders when getOrLoad finishes loading
    // (otherwise useEffect never re-runs after stub→loaded — breaks admin portal preview).
    const assetSyncState = asset?.syncState;
    const assetLoadedId = asset?.id;
    const [url, setUrl] = useState(resolvedDirect);
    useEffect(() => {
        if (resolvedDirect) {
            setUrl((prev) => (prev === resolvedDirect ? prev : resolvedDirect));
            return undefined;
        }
        let cancelled = false;
        if (!asset) {
            setUrl(undefined);
            return () => { cancelled = true; };
        }
        const sync = async () => {
            if (asset.syncState && asset.syncState !== 'loaded')
                return;
            const precached = asset.getPrecachedUrl?.('original');
            if (precached) {
                if (!cancelled)
                    setUrl((prev) => (prev === precached ? prev : precached));
                return;
            }
            if (typeof asset.getSignedUrl === 'function') {
                const signed = await asset.getSignedUrl('original');
                const next = signed?.url || '';
                if (!cancelled)
                    setUrl((prev) => (prev === next ? prev : next));
            }
        };
        void sync();
        return () => { cancelled = true; };
    }, [asset, assetSyncState, assetLoadedId, resolvedDirect]);
    if (!url) {
        return (_jsx(Box, { sx: wizardImagePaneInvisibleReserveSx, "aria-hidden": true }));
    }
    return (_jsx(Box, { sx: wizardImagePaneLayoutSx, children: _jsx(Box, { component: "img", src: url, alt: "", sx: {
                maxWidth: '100%',
                maxHeight: 400,
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
                border: 0,
                outline: 'none',
                boxShadow: 'none',
            } }) }));
});
/**
 * Builds a plain-text summary of answers for the AI profile and knowledge base search.
 */
function buildSummary(answers, steps) {
    const lines = [
        'Preference summary – use this to recommend or respond:',
        '',
    ];
    steps.forEach((step) => {
        if (isShoeSizeStep(step)) {
            const gender = answers[`${step.id}_gender`];
            const size = answers[`${step.id}_size`];
            const width = answers[`${step.id}_width`];
            if (gender || size || width) {
                lines.push(`Shoe fit: Gender ${gender || '—'}, Size ${size || '—'}, Width ${width || '—'}`);
            }
            return;
        }
        const value = answers[step.id];
        if (value == null)
            return;
        const q = step;
        const label = getFormattedTextPlain(q.label).replace(/\?$/, '');
        if (Array.isArray(value)) {
            if (value.length > 0) {
                lines.push(`${label}: ${value.join(', ')}`);
            }
        }
        else {
            lines.push(`${label}: ${value}`);
        }
    });
    lines.push('');
    lines.push('Use these preferences to respond or recommend.');
    return lines.join('\n');
}
function clampWizardStepIndex(idx, totalSteps) {
    if (totalSteps <= 0)
        return 0;
    return Math.min(Math.max(0, idx), totalSteps - 1);
}
const WizardTool = ({ steps = defaultSteps, initialAnswers, initialStepIndex, onComplete, onClose, onProgress, fillAvailableHeight = false, }) => {
    const [stepIndex, setStepIndex] = useState(() => clampWizardStepIndex(initialStepIndex ?? 0, steps.length));
    const [answers, setAnswers] = useState(() => ({ ...(initialAnswers ?? {}) }));
    /** Hovered option `value` on standard question steps (chips). */
    const [hoveredOptionValue, setHoveredOptionValue] = useState(null);
    /** Hovered shoe chip: gender or width option value; size uses plain strings — no hover metadata. */
    const [hoveredShoeField, setHoveredShoeField] = useState(null);
    const [hoveredShoeOptionValue, setHoveredShoeOptionValue] = useState(null);
    /** Parent often passes a new inline onProgress each render; do not list it in useEffect deps (infinite loop). */
    const onProgressRef = useRef(onProgress);
    onProgressRef.current = onProgress;
    useEffect(() => {
        setStepIndex((i) => clampWizardStepIndex(i, steps.length));
    }, [steps.length]);
    useEffect(() => {
        onProgressRef.current?.(stepIndex, steps.length, answers);
    }, [stepIndex, answers, steps.length]);
    useEffect(() => {
        setHoveredOptionValue(null);
        setHoveredShoeField(null);
        setHoveredShoeOptionValue(null);
    }, [stepIndex]);
    const stepBodyScrollRef = useRef(null);
    useLayoutEffect(() => {
        const el = stepBodyScrollRef.current;
        if (el)
            el.scrollTop = 0;
    }, [stepIndex]);
    const step = steps[stepIndex];
    const isFirst = stepIndex === 0;
    const isLast = stepIndex === steps.length - 1;
    const reserveImagePane = useMemo(() => wizardHasAnyConfiguredImageMedia(steps), [steps]);
    const setAnswer = useCallback((id, value) => {
        setAnswers((prev) => ({ ...prev, [id]: value }));
    }, []);
    const handleNext = useCallback(() => {
        if (isLast) {
            onComplete(buildSummary(answers, steps), answers);
            return;
        }
        setStepIndex((i) => Math.min(i + 1, steps.length - 1));
    }, [isLast, answers, steps, onComplete]);
    const handleBack = useCallback(() => {
        setStepIndex((i) => Math.max(0, i - 1));
    }, []);
    const canProceed = () => {
        if (isShoeSizeStep(step)) {
            const gender = answers[`${step.id}_gender`];
            const size = answers[`${step.id}_size`];
            const width = answers[`${step.id}_width`];
            return Boolean(gender && size && width);
        }
        const value = answers[step.id];
        if (step.selectType !== 'multiple') {
            return typeof value === 'string' && value.length > 0;
        }
        return Array.isArray(value) && value.length > 0;
    };
    const handleSelectAll = useCallback(() => {
        if (!('options' in step))
            return;
        const q = step;
        if (q.selectType !== 'multiple')
            return;
        const allValues = q.options.filter((o) => o.value !== 'SELECT_ALL').map((o) => o.value);
        setAnswer(step.id, allValues);
    }, [step, setAnswer]);
    const renderFormattedLabel = (value, variant = 'h6') => {
        const html = sanitizeFormattedTextHtml(getFormattedTextHtml(value));
        if (html) {
            return (_jsx(Typography, { variant: variant, sx: { fontWeight: variant === 'h6' ? 600 : undefined }, component: "div", dangerouslySetInnerHTML: { __html: html } }));
        }
        return (_jsx(Typography, { variant: variant, sx: { fontWeight: variant === 'h6' ? 600 : undefined }, children: getFormattedTextPlain(value) }));
    };
    const renderQuestionStep = (q) => {
        const value = answers[q.id];
        const isMulti = q.selectType === 'multiple';
        const selectedSet = Array.isArray(value) ? new Set(value) : new Set();
        const hoveredOpt = hoveredOptionValue && hoveredOptionValue !== 'SELECT_ALL'
            ? q.options.find((o) => o.value === hoveredOptionValue)
            : undefined;
        const baseImg = resolveQuestionDisplayImage(q, value);
        const hoverImg = hoveredOpt ? resolveOptionOrQuestionImage(q, hoveredOpt) : null;
        const displayImage = hoverImg != null && !imageKeysEqual(baseImg, hoverImg) ? hoverImg : baseImg;
        const descriptionBelowChips = hoveredOpt
            ? hoveredOpt.description?.trim() ?? ''
            : selectedOptionDescription(q, value);
        const questionBody = (_jsxs(Stack, { spacing: 2, sx: { flex: { xs: '1 1 auto', md: '2 1 0%' }, minWidth: 0 }, children: [renderFormattedLabel(q.label, 'h6'), _jsxs(Box, { onMouseLeave: () => setHoveredOptionValue(null), sx: { width: '100%' }, children: [_jsx(Stack, { direction: "row", flexWrap: "wrap", useFlexGap: true, spacing: 1, children: q.options.map((opt) => {
                                if (opt.value === 'SELECT_ALL') {
                                    return (_jsx(Chip, { label: opt.label, onClick: handleSelectAll, onMouseEnter: () => setHoveredOptionValue(null), variant: "outlined", sx: { textTransform: 'none' }, className: "site-system-chip-primary" }, opt.value));
                                }
                                const selected = isMulti
                                    ? selectedSet.has(opt.value)
                                    : value === opt.value;
                                const toggle = () => {
                                    if (isMulti) {
                                        const next = selectedSet.has(opt.value)
                                            ? [...selectedSet].filter((v) => v !== opt.value)
                                            : [...selectedSet, opt.value];
                                        setAnswer(q.id, next);
                                    }
                                    else {
                                        setAnswer(q.id, opt.value);
                                    }
                                };
                                const desc = opt.description?.trim();
                                return (_jsx(Chip, { label: opt.label, title: desc || undefined, onClick: toggle, onMouseEnter: () => setHoveredOptionValue(opt.value), icon: selected ? _jsx(CheckIcon, { sx: { fontSize: 18, color: 'inherit' } }) : undefined, variant: selected ? 'filled' : 'outlined', className: "site-system-chip-primary", sx: {
                                        textTransform: 'none',
                                    } }, opt.value));
                            }) }), _jsx(Typography, { component: "div", variant: "body2", sx: {
                                mt: 1,
                                minHeight: '4.5em',
                                lineHeight: 1.5,
                                color: 'text.primary',
                            }, children: descriptionBelowChips || '\u00a0' })] })] }));
        const showImageSlot = reserveImagePane
            || Boolean(displayImage.directUrl || displayImage.assetKey);
        return (_jsxs(Stack, { direction: { xs: 'column', md: 'row' }, spacing: 2, alignItems: "flex-start", sx: { width: '100%' }, children: [questionBody, showImageSlot ? (_jsx(WizardStepImage, { directUrl: displayImage.directUrl, assetKey: displayImage.assetKey })) : null] }));
    };
    const renderShoeSizeStep = (s) => {
        const gender = answers[`${s.id}_gender`] || '';
        const size = answers[`${s.id}_size`] || '';
        const width = answers[`${s.id}_width`] || '';
        const sizes = gender ? (s.sizeOptions[gender] ?? []) : [];
        const widths = gender ? (s.widthOptions[gender] ?? []) : [];
        const shoeImageKey = imageAssetKeyFromConfig(s);
        const shoeImageUrl = s.imageUrl?.trim();
        let hoveredShoeOpt;
        if (hoveredShoeField === 'gender' && hoveredShoeOptionValue) {
            hoveredShoeOpt = s.genderOptions.find((o) => o.value === hoveredShoeOptionValue);
        }
        else if (hoveredShoeField === 'width' && gender && hoveredShoeOptionValue) {
            hoveredShoeOpt = (s.widthOptions[gender] ?? []).find((o) => o.value === hoveredShoeOptionValue);
        }
        const baseShoeImg = { directUrl: shoeImageUrl, assetKey: shoeImageKey };
        const hoverShoeImg = hoveredShoeOpt ? resolveShoeOptionOrStepImage(s, hoveredShoeOpt) : null;
        const shoeDisplayImage = hoverShoeImg != null && !imageKeysEqual(baseShoeImg, hoverShoeImg) ? hoverShoeImg : baseShoeImg;
        const shoeDescriptionBelow = hoveredShoeOpt
            ? hoveredShoeOpt.description?.trim() ?? ''
            : (() => {
                const g = s.genderOptions.find((o) => o.value === gender);
                if (g?.description?.trim())
                    return g.description.trim();
                if (gender && width) {
                    const w = (s.widthOptions[gender] ?? []).find((o) => o.value === width);
                    if (w?.description?.trim())
                        return w.description.trim();
                }
                return '';
            })();
        const clearShoeHover = () => {
            setHoveredShoeField(null);
            setHoveredShoeOptionValue(null);
        };
        const body = (_jsxs(Stack, { spacing: 2, sx: { flex: { xs: '1 1 auto', md: '2 1 0%' }, minWidth: 0 }, children: [renderFormattedLabel(s.label, 'h6'), _jsxs(Box, { onMouseLeave: clearShoeHover, sx: { width: '100%' }, children: [_jsx(Typography, { variant: "subtitle2", color: "text.secondary", children: "Gender" }), _jsx(Stack, { direction: "row", flexWrap: "wrap", useFlexGap: true, spacing: 1, children: s.genderOptions.map((opt) => {
                                const selected = gender === opt.value;
                                const desc = opt.description?.trim();
                                return (_jsx(Chip, { label: opt.label, title: desc || undefined, onClick: () => {
                                        setAnswer(`${s.id}_gender`, opt.value);
                                        setAnswer(`${s.id}_size`, '');
                                        setAnswer(`${s.id}_width`, '');
                                    }, onMouseEnter: () => {
                                        setHoveredShoeField('gender');
                                        setHoveredShoeOptionValue(opt.value);
                                    }, icon: selected ? _jsx(CheckIcon, { sx: { fontSize: 18, color: 'inherit' } }) : undefined, variant: selected ? 'filled' : 'outlined', className: "site-system-chip-primary" }, opt.value));
                            }) }), gender && sizes.length > 0 && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "subtitle2", color: "text.secondary", sx: { mt: 1 }, children: "Size" }), _jsx(Stack, { direction: "row", flexWrap: "wrap", useFlexGap: true, spacing: 1, children: sizes.map((v) => {
                                        const selected = size === v;
                                        return (_jsx(Chip, { label: v, onClick: () => setAnswer(`${s.id}_size`, v), onMouseEnter: clearShoeHover, icon: selected ? _jsx(CheckIcon, { sx: { fontSize: 18, color: 'inherit' } }) : undefined, variant: selected ? 'filled' : 'outlined', className: "site-system-chip-primary" }, v));
                                    }) })] })), gender && widths.length > 0 && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "subtitle2", color: "text.secondary", sx: { mt: 1 }, children: "Width" }), _jsx(Stack, { direction: "row", flexWrap: "wrap", useFlexGap: true, spacing: 1, children: widths.map((opt) => {
                                        const selected = width === opt.value;
                                        const desc = opt.description?.trim();
                                        return (_jsx(Chip, { label: opt.label, title: desc || undefined, onClick: () => setAnswer(`${s.id}_width`, opt.value), onMouseEnter: () => {
                                                setHoveredShoeField('width');
                                                setHoveredShoeOptionValue(opt.value);
                                            }, icon: selected ? _jsx(CheckIcon, { sx: { fontSize: 18, color: 'inherit' } }) : undefined, variant: selected ? 'filled' : 'outlined', className: "site-system-chip-primary" }, opt.value));
                                    }) })] })), _jsx(Typography, { component: "div", variant: "body2", sx: {
                                mt: 1,
                                minHeight: '4.5em',
                                lineHeight: 1.5,
                                color: 'text.primary',
                            }, children: shoeDescriptionBelow || '\u00a0' })] })] }));
        const showShoeImageSlot = reserveImagePane
            || Boolean(shoeDisplayImage.directUrl || shoeDisplayImage.assetKey);
        return (_jsxs(Stack, { direction: { xs: 'column', md: 'row' }, spacing: 2, alignItems: "flex-start", sx: { width: '100%' }, children: [body, showShoeImageSlot ? (_jsx(WizardStepImage, { directUrl: shoeDisplayImage.directUrl, assetKey: shoeDisplayImage.assetKey })) : null] }));
    };
    return (_jsxs(Box, { sx: {
            p: 2,
            borderRadius: 1,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            color: 'text.primary',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            flex: fillAvailableHeight ? 1 : '0 1 auto',
            alignSelf: 'stretch',
            minHeight: 0,
            width: '100%',
            maxHeight: '100%',
            overflow: 'hidden',
        }, children: [_jsxs(Typography, { variant: "subtitle2", sx: {
                    flexShrink: 0,
                    mb: 1,
                    display: 'block',
                    color: 'text.primary',
                    fontWeight: 600,
                }, children: ["Step ", stepIndex + 1, " of ", steps.length] }), _jsx(Box, { ref: stepBodyScrollRef, sx: wizardStepBodyScrollSx, children: isShoeSizeStep(step)
                    ? renderShoeSizeStep(step)
                    : renderQuestionStep(step) }), _jsxs(Stack, { direction: "row", spacing: 1, sx: {
                    flexShrink: 0,
                    pt: 1.5,
                    mt: 0,
                    mb: '10px',
                    alignSelf: 'stretch',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                }, justifyContent: "space-between", children: [_jsxs(Stack, { direction: "row", spacing: 1, children: [_jsx(Button, { startIcon: _jsx(BackIcon, {}), onClick: handleBack, disabled: isFirst, size: "small", className: "site-system-button-secondary", children: "Back" }), onClose && (_jsx(Button, { onClick: onClose, size: "small", className: "site-system-button-secondary", children: "Cancel" }))] }), _jsx(Button, { variant: "contained", endIcon: isLast ? undefined : _jsx(NextIcon, {}), onClick: handleNext, disabled: !canProceed(), size: "small", className: "site-system-button-primary", children: isLast ? 'Complete' : 'Next' })] })] }));
};
export default WizardTool;
//# sourceMappingURL=WizardTool.js.map